import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  Pressable,
  Switch,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CityDistrictSelect } from '../../components/CityDistrictSelect';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ModalBottomSheet } from '../../components/ModalBottomSheet';
import { MonthYearSelect } from '../../components/MonthYearSelect';
import { useAuth } from '../../contexts/AuthContext';
import { useAppTheme } from '../../contexts/ThemeContext';
import type { MainStackParamList } from '../../navigation/types';
import {
  clearPendingPhoneLink,
  confirmLinkPhoneVerificationCode,
  sendLinkPhoneVerification,
} from '../../services/authService';
import { submitSupportMessage } from '../../services/supportService';
import { ACCOUNT_DELETION_GRACE_DAYS, updateUserProfile } from '../../services/userService';
import type { BabyGender, UserProfile, UserRole } from '../../types/models';
import { syncLocationFromProfile } from '../../utils/turkeyData';
import { birthYearOptions } from '../../utils/birthPickerData';

const ROLES: { key: UserRole; label: string }[] = [
  { key: 'mother', label: 'Anne' },
  { key: 'father', label: 'Baba' },
  { key: 'caregiver', label: 'Bakıcı' },
];

type Props = NativeStackScreenProps<MainStackParamList, 'ProfileEdit'>;

function applyProfileToForm(
  profile: UserProfile,
  setters: {
    setRole: (r: UserRole) => void;
    setDisplayName: (s: string) => void;
    setParentAge: (s: string) => void;
    setBirthMonth: (s: string) => void;
    setBirthYear: (s: string) => void;
    setBabyGender: (g: BabyGender) => void;
    setCity: (s: string) => void;
    setDistrict: (s: string) => void;
  },
) {
  const d = profile.babyBirthDate.toDate();
  setters.setRole(profile.role);
  setters.setDisplayName(profile.displayName ?? '');
  setters.setParentAge(String(profile.parentAge ?? ''));
  setters.setBirthMonth(String(d.getMonth() + 1));
  setters.setBirthYear(String(d.getFullYear()));
  setters.setBabyGender(profile.babyGender === 'boy' ? 'boy' : 'girl');
  const loc = syncLocationFromProfile(profile.city ?? '', profile.district ?? '');
  setters.setCity(loc.city);
  setters.setDistrict(loc.district);
}

function userSignedInWithEmail(user: { providerData: { providerId: string }[] } | null): boolean {
  return Boolean(user?.providerData?.some(p => p.providerId === 'password'));
}

export function ProfileEditScreen({ navigation }: Props) {
  const insets = useSafeAreaInsets();
  const { user, profile, profileLoading, refreshProfile, logout, deleteMyAccount } = useAuth();
  const { nightWatchEnabled, setNightWatchEnabled, isDark } = useAppTheme();

  const [role, setRole] = useState<UserRole>('mother');
  const [displayName, setDisplayName] = useState('');
  const [parentAge, setParentAge] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [babyGender, setBabyGender] = useState<BabyGender>('girl');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [phoneE164, setPhoneE164] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [phoneStep, setPhoneStep] = useState<'phone' | 'code'>('phone');
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [deleteAccountBusy, setDeleteAccountBusy] = useState(false);

  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportDraft, setSupportDraft] = useState('');
  const [supportBusy, setSupportBusy] = useState(false);
  const [supportError, setSupportError] = useState<string | null>(null);

  const allowedYears = useMemo(() => new Set(birthYearOptions()), []);

  const needsPhoneVerification =
    Boolean(user) &&
    userSignedInWithEmail(user) &&
    !(user?.phoneNumber && user.phoneNumber.length > 0);

  useFocusEffect(
    useCallback(() => {
      if (!profile) {
        return;
      }
      applyProfileToForm(profile, {
        setRole,
        setDisplayName,
        setParentAge,
        setBirthMonth,
        setBirthYear,
        setBabyGender,
        setCity,
        setDistrict,
      });
    }, [profile]),
  );

  useEffect(() => {
    if (!toast) {
      return;
    }
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    return () => {
      clearPendingPhoneLink();
    };
  }, []);

  const canSubmit = useMemo(() => {
    const dn = displayName.trim();
    const c = city.trim();
    const d = district.trim();
    const m = parseInt(birthMonth, 10);
    const y = birthYear.trim();
    const pa = parseInt(parentAge, 10);
    if (!dn || !c || !d || !birthMonth || !y) {
      return false;
    }
    if (Number.isNaN(pa) || pa < 16 || pa > 80) {
      return false;
    }
    if (Number.isNaN(m) || m < 1 || m > 12) {
      return false;
    }
    if (!allowedYears.has(y)) {
      return false;
    }
    return true;
  }, [displayName, city, district, birthMonth, birthYear, parentAge, allowedYears]);

  async function onSubmit() {
    if (!user?.uid || !canSubmit) {
      return;
    }
    setError(null);
    const y = parseInt(birthYear, 10);
    const m = parseInt(birthMonth, 10);
    const pa = parseInt(parentAge, 10);
    const babyBirthDate = new Date(y, m - 1, 1);
    setBusy(true);
    try {
      await updateUserProfile(user.uid, {
        role,
        displayName: displayName.trim(),
        parentAge: pa,
        babyBirthDate,
        babyGender,
        city: city.trim(),
        district: district.trim(),
      });
      await refreshProfile();
      setToast('Profilin güncellendi');
      setTimeout(() => {
        navigation.goBack();
      }, 1600);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Güncelleme başarısız';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  function openPhoneModal() {
    setPhoneError(null);
    setPhoneStep('phone');
    setOtpCode('');
    setPhoneE164(user?.phoneNumber?.trim() || '+90');
    setPhoneModalOpen(true);
  }

  async function onSendPhoneLinkSms() {
    if (!user) {
      return;
    }
    const raw = phoneE164.trim();
    if (!raw.startsWith('+') || raw.length < 10) {
      setPhoneError('Ülke kodu ile birlikte girin (örn. +905551234567).');
      return;
    }
    setPhoneBusy(true);
    setPhoneError(null);
    try {
      await sendLinkPhoneVerification(user, raw);
      setPhoneStep('code');
    } catch (e: unknown) {
      setPhoneError(e instanceof Error ? e.message : 'SMS gönderilemedi.');
    } finally {
      setPhoneBusy(false);
    }
  }

  async function onConfirmPhoneOtp() {
    setPhoneBusy(true);
    setPhoneError(null);
    try {
      await confirmLinkPhoneVerificationCode(otpCode);
      setPhoneModalOpen(false);
      setToast('Telefon hesabına bağlandı');
      await refreshProfile();
    } catch (e: unknown) {
      setPhoneError(e instanceof Error ? e.message : 'Kod doğrulanamadı.');
    } finally {
      setPhoneBusy(false);
    }
  }

  if (!user) {
    return null;
  }

  if (profileLoading && !profile) {
    return (
 <View className="flex-1 items-center justify-center bg-white dark:bg-black">
        <ActivityIndicator size="large" color="#18181b" />
 <Text className="mt-3 text-sm text-gray-500 dark:text-zinc-400">Profil yükleniyor…</Text>
      </View>
    );
  }

  if (!profile) {
    return (
 <View className="flex-1 items-center justify-center bg-white dark:bg-black px-6 dark:bg-black">
 <Text className="text-center text-gray-600 dark:text-zinc-400">Profil bulunamadı.</Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
 className="flex-1 bg-white dark:bg-black">
      {toast ? (
        <View
          className="absolute left-4 right-4 z-50 rounded-2xl border border-zinc-300 bg-white px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900"
          style={{ top: 12 + insets.top }}>
          <Text className="text-center text-sm font-semibold text-zinc-700 dark:text-zinc-200">
            {toast}
          </Text>
        </View>
      ) : null}

      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 24,
          paddingTop: 24,
          paddingBottom: 120 + insets.bottom,
        }}>

 <View className="mb-5 rounded-2xl border border-zinc-200 bg-white px-4 py-4 dark:border-zinc-800 dark:bg-zinc-900">
 <View className="flex-row items-center justify-between gap-3">
 <View className="min-w-0 flex-1">
 <Text className="text-base font-semibold text-gray-800 dark:text-zinc-100">
                Gece Nöbeti Modu
              </Text>
 <Text className="mt-1 text-xs leading-5 text-gray-500 dark:text-zinc-400">
                Açıkken koyu, kapalıyken açık tema kullanılır. Telefonun sistem temasına bakılmaz.
              </Text>
            </View>
            <Switch
              accessibilityLabel="Gece nöbeti modu"
              value={nightWatchEnabled}
              onValueChange={setNightWatchEnabled}
              trackColor={{ false: '#e4e4e7', true: '#52525b' }}
              thumbColor={nightWatchEnabled ? '#fafafa' : '#ffffff'}
            />
          </View>
        </View>

        <View className="mb-5 rounded-2xl border border-zinc-200 bg-white px-4 py-4 dark:border-zinc-800 dark:bg-zinc-900">
          <Text className="text-base font-semibold text-gray-800 dark:text-zinc-100">
            İletişim / Destek
          </Text>
          <Text className="mt-1 text-xs leading-5 text-gray-500 dark:text-zinc-400">
            Acil durum, öneri veya teknik sorunlar için mesajını güvenli şekilde ilet.
          </Text>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel="Destek mesajı gönder"
            activeOpacity={0.88}
            onPress={() => {
              setSupportError(null);
              setSupportDraft('');
              setSupportModalOpen(true);
            }}
            className="mt-3 self-start rounded-xl bg-zinc-900 px-4 py-2.5 dark:bg-zinc-100">
            <Text className="text-sm font-semibold text-white dark:text-zinc-950">Mesaj gönder</Text>
          </TouchableOpacity>
        </View>

        {error ? (
          <View className="mb-4 rounded-2xl border border-zinc-300 bg-zinc-100 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900">
            <Text className="text-center text-sm text-zinc-800 dark:text-zinc-200">{error}</Text>
          </View>
        ) : null}

        {needsPhoneVerification ? (
 <View className="mb-5 rounded-2xl border border-zinc-300 bg-zinc-50 dark:bg-zinc-900 px-4 py-4 ">
 <Text className="mb-2 text-sm font-semibold text-zinc-900">Telefon doğrulaması</Text>
 <Text className="mb-3 text-xs leading-5 text-zinc-900/80">
              Pazar yeri ilanları için hesabında doğrulanmış bir telefon numarası gerekir. E-posta ile
              giriş yaptığın için numaranı SMS ile bağlamanı öneririz.
            </Text>
            <TouchableOpacity
              onPress={openPhoneModal}
              activeOpacity={0.88}
              className="rounded-xl bg-zinc-900 py-2.5 dark:bg-zinc-50">
              <Text className="text-center text-sm font-semibold text-white dark:text-zinc-950">
                Telefonunu Doğrula
              </Text>
            </TouchableOpacity>
          </View>
        ) : null}

        <Text className="mb-2 font-medium text-zinc-900 dark:text-zinc-100">Rolün</Text>
 <View className="mb-5 flex-row flex-wrap gap-2">
          {ROLES.map(r => (
            <TouchableOpacity
              key={r.key}
              activeOpacity={0.85}
              onPress={() => setRole(r.key)}
              className={`min-w-[28%] flex-1 rounded-xl border px-3 py-3 ${
                role === r.key
                  ? 'border-zinc-900 bg-zinc-900 dark:border-zinc-50 dark:bg-zinc-50'
                  : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900'
              }`}>
              <Text
                className={`text-center text-sm font-semibold ${
                  role === r.key ? 'text-white dark:text-zinc-950' : 'text-zinc-800 dark:text-zinc-200'
                }`}>
                {r.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <Text className="mb-2 font-medium text-zinc-900 dark:text-zinc-100">
          Gösterilecek isim (rumuz veya ad)
        </Text>
        <TextInput
          className="mb-4 border-b border-zinc-200 py-2 text-base text-zinc-900 dark:border-zinc-700 dark:text-zinc-100"
          placeholder="Örn. Can'ın annesi"
          placeholderTextColor="#9ca3af"
          value={displayName}
          onChangeText={t => {
            setError(null);
            setDisplayName(t);
          }}
        />

        <Text className="mb-2 font-medium text-zinc-900 dark:text-zinc-100">Ebeveyn yaşı</Text>
        <TextInput
          className="mb-4 border-b border-zinc-200 py-2 text-base text-zinc-900 dark:border-zinc-700 dark:text-zinc-100"
          placeholder="Örn. 32"
          placeholderTextColor="#9ca3af"
          keyboardType="number-pad"
          value={parentAge}
          onChangeText={t => {
            setError(null);
            setParentAge(t);
          }}
        />

        <MonthYearSelect
          month={birthMonth}
          year={birthYear}
          onMonthChange={m => {
            setError(null);
            setBirthMonth(m);
          }}
          onYearChange={y => {
            setError(null);
            setBirthYear(y);
          }}
        />

        <Text className="mb-2 font-medium text-zinc-900 dark:text-zinc-100">Bebek cinsiyeti</Text>
 <View className="mb-5 flex-row flex-wrap gap-2">
          {(
            [
              { g: 'girl' as const, label: 'Kız' },
              { g: 'boy' as const, label: 'Erkek' },
            ] as const
          ).map(({ g, label }) => (
            <TouchableOpacity
              key={g}
              activeOpacity={0.85}
              onPress={() => setBabyGender(g)}
              className={`min-w-[28%] flex-1 rounded-xl border py-3 ${
                babyGender === g
                  ? 'border-zinc-900 bg-zinc-900 dark:border-zinc-50 dark:bg-zinc-50'
                  : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900'
              }`}>
              <Text
                className={`text-center text-sm font-semibold ${
                  babyGender === g ? 'text-white dark:text-zinc-950' : 'text-zinc-800 dark:text-zinc-200'
                }`}>
                {label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <CityDistrictSelect
          city={city}
          district={district}
          onCityChange={c => {
            setError(null);
            setCity(c);
          }}
          onDistrictChange={d => {
            setError(null);
            setDistrict(d);
          }}
          theme="rose"
        />

        <TouchableOpacity
          activeOpacity={0.85}
          onPress={onSubmit}
          disabled={!canSubmit || busy}
          className={`rounded-xl border border-zinc-900 py-3 dark:border-zinc-50 ${
            !canSubmit || busy ? 'bg-zinc-300 dark:bg-zinc-700' : 'bg-zinc-900 dark:bg-zinc-50'
          }`}>
          <Text className="text-center text-base font-semibold text-white dark:text-zinc-950">
            {busy ? 'Güncelleniyor…' : 'Güncelle'}
          </Text>
        </TouchableOpacity>

        {!user?.isAnonymous ? (
          <Pressable
            onPress={() => setDeleteAccountModalOpen(true)}
            className="mt-8 rounded-xl border border-red-200 bg-red-50/80 py-2.5 active:opacity-80 dark:border-red-900/60 dark:bg-red-950/30">
            <Text className="text-center text-base font-semibold text-red-700 dark:text-red-400">
              Hesabımı sil
            </Text>
          </Pressable>
        ) : null}

        <Pressable
          onPress={() => void logout()}
          className={`rounded-xl border border-zinc-300/80 bg-transparent py-2.5 active:opacity-80 dark:border-zinc-700 ${
            user?.isAnonymous ? 'mt-8' : 'mt-4'
          }`}>
 <Text className="text-center text-base font-medium text-zinc-500/90">Çıkış Yap</Text>
        </Pressable>
      </ScrollView>

      <ConfirmModal
        visible={deleteAccountModalOpen}
        title="Hesabını kapat?"
        message={`Üyeliğiniz askıya alınır ve çıkış yapılır. ${ACCOUNT_DELETION_GRACE_DAYS} gün içinde tekrar giriş yaparak hesabınıza devam edebilirsiniz; bu sürede giriş yapmazsanız hesabınız tamamen kaldırılır.`}
        confirmLabel={deleteAccountBusy ? 'İşleniyor…' : 'Evet, kapat'}
        cancelLabel="Vazgeç"
        destructive
        onConfirm={() => {
          if (deleteAccountBusy) {
            return;
          }
          setDeleteAccountBusy(true);
          void deleteMyAccount()
            .then(() => setDeleteAccountModalOpen(false))
            .catch(() => {
              setError('Hesap kapatılamadı. Bağlantınızı kontrol edip yeniden deneyin.');
              setDeleteAccountModalOpen(false);
            })
            .finally(() => setDeleteAccountBusy(false));
        }}
        onCancel={() => {
          if (!deleteAccountBusy) {
            setDeleteAccountModalOpen(false);
          }
        }}
      />

      <ModalBottomSheet
        visible={phoneModalOpen}
        onRequestClose={() => !phoneBusy && setPhoneModalOpen(false)}
        dismissDisabled={phoneBusy}
        keyboardAvoiding>
        <View
          className="rounded-t-3xl bg-white px-5 pt-6 dark:bg-zinc-900"
          style={{ paddingBottom: 24 + insets.bottom }}>
          <Text className="mb-4 text-xl font-bold text-gray-800 dark:text-zinc-100">
            {phoneStep === 'phone' ? 'Telefon numarası' : 'SMS kodu'}
          </Text>
          <Text className="mb-4 text-xs leading-5 text-gray-500 dark:text-zinc-400">
            İlan vermek için doğrulanmış telefon şarttır. Numaran yalnızca güvenlik amaçlı kullanılır.
          </Text>
          {phoneStep === 'phone' ? (
            <TextInput
              value={phoneE164}
              onChangeText={setPhoneE164}
              placeholder="+905551234567"
              placeholderTextColor="#9ca3af"
              keyboardType="phone-pad"
              autoCapitalize="none"
              className="mb-3 border-b border-zinc-200 py-2 text-base text-gray-800 dark:border-zinc-700 dark:text-zinc-100"
            />
          ) : (
            <TextInput
              value={otpCode}
              onChangeText={setOtpCode}
              placeholder="6 haneli kod"
              placeholderTextColor="#9ca3af"
              keyboardType="number-pad"
              maxLength={8}
              className="mb-3 border-b border-zinc-200 py-2 text-base text-gray-800 dark:border-zinc-700 dark:text-zinc-100"
            />
          )}
          {phoneError ? (
            <Text className="mb-3 text-center text-sm text-zinc-700">{phoneError}</Text>
          ) : null}
          <View className="flex-row gap-3">
            <TouchableOpacity
              disabled={phoneBusy}
              onPress={() => {
                clearPendingPhoneLink();
                setPhoneModalOpen(false);
              }}
              className="flex-1 rounded-xl border border-zinc-300 bg-white py-2.5 dark:border-zinc-800 dark:bg-zinc-900">
              <Text className="text-center text-sm font-semibold text-gray-600 dark:text-zinc-300">
                Vazgeç
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              disabled={phoneBusy}
              onPress={() =>
                void (phoneStep === 'phone' ? onSendPhoneLinkSms() : onConfirmPhoneOtp())
              }
              className="flex-1 rounded-xl border border-zinc-900 bg-zinc-900 py-2.5 dark:border-zinc-50 dark:bg-zinc-50">
              {phoneBusy ? (
                <ActivityIndicator color={isDark ? '#18181b' : '#fff'} />
              ) : (
                <Text className="text-center font-semibold text-white dark:text-zinc-950">
                  {phoneStep === 'phone' ? 'Kod gönder' : 'Doğrula'}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ModalBottomSheet>

      <ModalBottomSheet
        visible={supportModalOpen}
        onRequestClose={() => !supportBusy && setSupportModalOpen(false)}
        dismissDisabled={supportBusy}
        keyboardAvoiding>
        <View
          className="rounded-t-3xl bg-white px-5 pt-6 dark:bg-zinc-900"
          style={{ paddingBottom: 24 + insets.bottom }}>
          <Text className="mb-4 text-xl font-bold text-gray-800 dark:text-zinc-100">Destek mesajı</Text>
          <Text className="mb-3 text-sm leading-5 text-gray-500 dark:text-zinc-400">
            Sorunu veya talebini kısaca yaz (en az 10 karakter). Kişisel veri veya üçüncü kişi hakkında hassas
            bilgi paylaşma.
          </Text>
          <Text className="mb-1 text-sm font-medium text-gray-700 dark:text-zinc-300">Mesajın</Text>
          <TextInput
            value={supportDraft}
            onChangeText={t => {
              setSupportDraft(t);
              setSupportError(null);
            }}
            placeholder="Detayı buraya yaz…"
            placeholderTextColor="#9ca3af"
            multiline
            textAlignVertical="top"
            editable={!supportBusy}
            maxLength={4000}
            className="mb-2 min-h-[100px] border-b border-zinc-200 py-2 text-base text-gray-800 dark:border-zinc-700 dark:text-zinc-100"
          />
          <Text className="mb-2 text-right text-xs text-zinc-400">
            {supportDraft.trim().length}/4000
          </Text>
          {supportError ? (
            <Text className="mb-3 text-center text-sm text-zinc-700">{supportError}</Text>
          ) : null}
          <View className="flex-row gap-3">
            <TouchableOpacity
              disabled={supportBusy}
              onPress={() => {
                if (!supportBusy) {
                  setSupportModalOpen(false);
                  setSupportDraft('');
                  setSupportError(null);
                }
              }}
              className="flex-1 rounded-xl border border-zinc-300 bg-white py-2.5 dark:border-zinc-800 dark:bg-zinc-900">
              <Text className="text-center text-sm font-semibold text-gray-600 dark:text-zinc-300">
                Vazgeç
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              disabled={supportBusy}
              onPress={() => {
                if (!user?.uid) {
                  return;
                }
                setSupportBusy(true);
                setSupportError(null);
                void (async () => {
                  try {
                    await submitSupportMessage({
                      userId: user.uid,
                      message: supportDraft,
                      displayNameSnapshot: profile.displayName,
                    });
                    setSupportModalOpen(false);
                    setSupportDraft('');
                    setToast('Mesajın iletildi. Teşekkürler.');
                  } catch (e) {
                    setSupportError(e instanceof Error ? e.message : 'Gönderilemedi.');
                  } finally {
                    setSupportBusy(false);
                  }
                })();
              }}
              className="flex-1 rounded-xl border border-zinc-900 bg-zinc-900 py-2.5 dark:border-zinc-50 dark:bg-zinc-50">
              {supportBusy ? (
                <ActivityIndicator color={isDark ? '#18181b' : '#fff'} />
              ) : (
                <Text className="text-center font-semibold text-white dark:text-zinc-950">Gönder</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ModalBottomSheet>
    </KeyboardAvoidingView>
  );
}
